import type { TovDefinitionEvidenceResponse } from "contracts/api/client-tov/evidence.responses";
import type { TovSourceResponse } from "contracts/api/client-tov/sources.responses";
import { For, Show } from "solid-js";
import { TOV_GUIDELINE_STATE, TOV_SOURCE_STATE } from "../../enums/tov";
import { Metric } from "../../ui/Metric";
import type { DefinitionCounts } from "../tov/guidelineCoverage";
import type {
  GuidelineCard,
  SourceDetail,
  SourceTrust,
} from "../tov/guidelineModel";
import { EmptyState, GuidelineCardGrid, SourceRow } from "./guidelineParts";

export function OverviewTab(props: {
  counts: DefinitionCounts;
  populatedCoverage: Array<{ label: string; value: number }>;
  quietEmptyCoverage: Array<{ label: string; value: number }>;
  cards: GuidelineCard[];
  sources: TovSourceResponse[];
  sourceDetails: Map<string, SourceDetail>;
  sourceCoverage: string;
  traceability: string;
  trust: SourceTrust;
  onInspect: (item: GuidelineCard, link: TovDefinitionEvidenceResponse) => void;
}) {
  const highlighted = () =>
    props.cards
      .filter((item) => item.sourceState === TOV_SOURCE_STATE.LINKED)
      .slice(0, 6);
  return (
    <div class="signal-guideline-section">
      <section class="signal-panel">
        <div class="signal-panel-head">
          <div>
            <span class="signal-mini-kicker">Guidance coverage</span>
            <h2>What guidance exists</h2>
            <p>
              Populated definition groups are prioritised; empty groups stay
              quiet.
            </p>
          </div>
        </div>
        <Show
          when={props.populatedCoverage.length > 0}
          fallback={
            <EmptyState
              title="No guidance definitions"
              text="No active definitions are stored on this version."
            />
          }
        >
          <div class="signal-coverage-grid">
            <For each={props.populatedCoverage}>
              {(row) => <Metric label={row.label} value={`${row.value}`} />}
            </For>
          </div>
        </Show>
        <Show when={props.quietEmptyCoverage.length > 0}>
          <p class="signal-muted signal-quiet-coverage">
            Not configured:{" "}
            {props.quietEmptyCoverage.map((row) => row.label).join(", ")}
          </p>
        </Show>
      </section>

      <section class="signal-panel">
        <div class="signal-panel-head">
          <div>
            <span class="signal-mini-kicker">Guidance highlights</span>
            <h2>Evidence-backed guidance</h2>
            <p>Only stored guidance with real linked evidence appears here.</p>
          </div>
        </div>
        <GuidelineCardGrid
          cards={highlighted()}
          emptyTitle="No linked evidence"
          emptyText="Guidance exists, but no evidence links are stored for the active version."
          onInspect={props.onInspect}
        />
      </section>

      <div class="signal-guideline-two-col">
        <section class="signal-panel">
          <div class="signal-panel-head">
            <div>
              <span class="signal-mini-kicker">Source coverage</span>
              <h2>Source and evidence state</h2>
            </div>
          </div>
          <div class="signal-source-summary-list">
            <Metric label="Sources" value={`${props.sources.length}`} />
            <Metric label="Sources processed" value={props.sourceCoverage} />
            <Metric
              label="Evidence source"
              value={
                props.trust.state === TOV_GUIDELINE_STATE.REVIEW
                  ? "Review required"
                  : props.trust.state === TOV_GUIDELINE_STATE.UNKNOWN
                    ? "Source not established"
                    : "Linked"
              }
            />
          </div>
          <For each={props.sources.slice(0, 3)}>
            {(source) => (
              <SourceRow
                source={source}
                detail={props.sourceDetails.get(source.id)}
                evidenceCount={
                  props.counts.evidenceBySource.get(source.id) ?? 0
                }
                trust={props.trust}
              />
            )}
          </For>
        </section>

        <section class="signal-panel">
          <div class="signal-panel-head">
            <div>
              <span class="signal-mini-kicker">Traceability</span>
              <h2>{props.traceability}</h2>
              <p>
                Current data links guidance to source evidence. Finding and page
                linkage appears only when the scan stores a traceable rule
                reference; no linkage is inferred.
              </p>
            </div>
          </div>
          <dl class="signal-trace-list">
            <div>
              <dt>Guidance to source evidence</dt>
              <dd>
                {props.cards.some(
                  (item) => item.sourceState === TOV_SOURCE_STATE.LINKED,
                )
                  ? "Available"
                  : props.cards.some(
                        (item) =>
                          item.sourceState === TOV_SOURCE_STATE.NOT_ESTABLISHED,
                      )
                    ? "Source not established"
                    : "No linked evidence"}
              </dd>
            </div>
            <div>
              <dt>Guidance to scan findings</dt>
              <dd>No active rule linkage</dd>
            </div>
            <div>
              <dt>Guidance to affected pages</dt>
              <dd>Not configured</dd>
            </div>
          </dl>
        </section>
      </div>
    </div>
  );
}
